import { state } from '../state';
import type { VehicleData } from '../api/types';
import { buildVehicleFaultCardHTML, buildDeviceNotificationCardHTML } from './fault-modal-cards';

function setText(id: string, value: string | number): void {
  const el = document.getElementById(id);
  if (el) el.textContent = String(value);
}

export function showFaultDetails(vehicle: VehicleData): void {
  state.currentFaultVehicle  = vehicle;
  state.activeFaultFilter    = 'all';

  document.getElementById('faultFilterAll')!.classList.add('active');
  document.getElementById('faultFilterVehicle')!.classList.remove('active');
  document.getElementById('faultFilterTelematics')!.classList.remove('active');
  setText('modalVehicleName', vehicle.device.name);

  // Summary strip + tab counts (computed once per open; filtering does not change them)
  const vehN = vehicle.faults.filter((f) => f.source === 'vehicle').length;
  const devN = vehicle.faults.filter((f) => f.source === 'telematics').length;
  setText('fmVehCount', vehN);
  setText('fmDevCount', devN);
  setText('fmTotalCount', vehN + devN);
  setText('fmTabAll', vehN + devN);
  setText('fmTabVeh', vehN);
  setText('fmTabDev', devN);

  renderFaultList();
  document.getElementById('faultModal')!.classList.add('show');
  // Move focus into the dialog for keyboard/screen-reader users
  (document.getElementById('modalClose') as HTMLElement | null)?.focus();
}

function emptyState(variant: 'healthy' | 'muted', title: string, sub: string): string {
  const icon = variant === 'healthy'
    ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>`
    : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>`;
  return `<div class="fm-empty">
      <div class="fm-empty-icon ${variant === 'muted' ? 'muted' : ''}">${icon}</div>
      <div class="fm-empty-title">${title}</div>
      <div class="fm-empty-sub">${sub}</div>
    </div>`;
}

function sectionHeader(label: string, variant: 'critical' | 'info'): string {
  return `<div class="fm-section fm-section--${variant}"><span class="fm-section-l">${label}</span><span class="fm-section-line"></span></div>`;
}

export function renderFaultList(): void {
  const faultList = document.getElementById('faultListContainer')!;
  faultList.innerHTML = '';
  if (!state.currentFaultVehicle) return;

  const faults = state.currentFaultVehicle.faults;
  if (faults.length === 0) {
    faultList.innerHTML = emptyState('healthy', 'No active faults', 'Nothing reported in the last 30 days.');
    return;
  }

  const vehicleFaults = faults
    .filter((f) => f.source === 'vehicle')
    .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
  const telemFaults = faults
    .filter((f) => f.source === 'telematics')
    .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());

  const toShow = state.activeFaultFilter === 'vehicle'
    ? vehicleFaults
    : state.activeFaultFilter === 'telematics'
    ? telemFaults
    : [...vehicleFaults, ...telemFaults];

  if (toShow.length === 0) {
    const sub = state.activeFaultFilter === 'vehicle'
      ? 'No ECU/vehicle faults in this period.'
      : 'No device notifications in this period.';
    faultList.innerHTML = emptyState('muted', 'Nothing in this category', sub);
    return;
  }

  let html = '';

  if (state.activeFaultFilter !== 'telematics') {
    if (state.activeFaultFilter === 'all' && vehicleFaults.length > 0) {
      html += sectionHeader(`Vehicle Faults (${vehicleFaults.length}) — Action Required`, 'critical');
    }
    vehicleFaults.forEach((fault) => {
      html += buildVehicleFaultCardHTML(fault);
    });
  }

  if (state.activeFaultFilter !== 'vehicle') {
    if (state.activeFaultFilter === 'all' && telemFaults.length > 0) {
      html += sectionHeader(`Device Notifications (${telemFaults.length}) — Informational`, 'info');
    }
    telemFaults.forEach((fault) => {
      html += buildDeviceNotificationCardHTML(fault);
    });
  }

  faultList.innerHTML = html;
}

export function initFaultModal(): void {
  document.getElementById('modalClose')!.addEventListener('click', () =>
    document.getElementById('faultModal')!.classList.remove('show')
  );
  document.getElementById('faultModal')!.addEventListener('click', (e) => {
    if ((e.target as Element).id === 'faultModal')
      document.getElementById('faultModal')!.classList.remove('show');
  });

  ['faultFilterAll', 'faultFilterVehicle', 'faultFilterTelematics'].forEach((id) => {
    document.getElementById(id)!.addEventListener('click', () => {
      state.activeFaultFilter =
        id === 'faultFilterAll' ? 'all' : id === 'faultFilterVehicle' ? 'vehicle' : 'telematics';
      document.getElementById('faultFilterAll')!.classList.toggle('active', state.activeFaultFilter === 'all');
      document.getElementById('faultFilterVehicle')!.classList.toggle('active', state.activeFaultFilter === 'vehicle');
      document.getElementById('faultFilterTelematics')!.classList.toggle('active', state.activeFaultFilter === 'telematics');
      renderFaultList();
    });
  });
}
